import { Calendar as CalendarIcon, Clock, MapPin, ArrowDown, Filter, FileText, ChevronRight } from "lucide-react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import { Button } from "../../components/ui/button"
import heroImage from "../../assets/TopHeroImages/3.jpg"
import { useMemo, useState } from "react"

export default function Calendrier() {
    const [activeFilter, setActiveFilter] = useState("All")

    const events = useMemo(() => [
        {
            month: "Septembre",
            id: "septembre",
            items: [
                { day: "15", title: "Rentrée Académique", type: "Administratif", detail: "Accueil des nouveaux étudiants et inscriptions administratives." },
                { day: "20", title: "Début des cours - Semestre 1", type: "Académique", detail: "Lancement officiel des enseignements magistraux et travaux dirigés du premier semestre." }
            ]
        },
        {
            month: "Décembre",
            id: "decembre",
            items: [
                { day: "15", title: "Début des examens partiels", type: "Examen", detail: "Sessions d'évaluations de mi-parcours pour l'ensemble des facultés." },
                { day: "23", title: "Vacances de Noël", type: "Congés", detail: "Période de fermeture annuelle pour les fêtes de fin d'année." }
            ]
        },
        {
            month: "Janvier",
            id: "janvier",
            items: [
                { day: "06", title: "Reprise des cours", type: "Académique", detail: "Retour en présentiel après la pause hivernale." }
            ]
        }
    ], [])

    const filterTypes = ["All", "Administratif", "Académique", "Examen", "Congés"]

    const filteredEvents = useMemo(() => {
        if (activeFilter === "All") return events;
        return events.map(monthGrp => ({
            ...monthGrp,
            items: monthGrp.items.filter(item => item.type === activeFilter)
        })).filter(monthGrp => monthGrp.items.length > 0);
    }, [activeFilter, events]);

    const getTypeStyles = (type) => {
        switch (type) {
            case 'Examen':
                return 'bg-accent/10 text-accent border-accent/20'
            case 'Congés':
                return 'bg-primary/10 text-primary border-primary/20'
            case 'Administratif':
                return 'bg-blue-500/10 text-blue-600 border-blue-500/20'
            default:
                return 'bg-secondary/50 text-secondary-foreground border-border'
        }
    }

    const navSections = events.map(m => ({ id: m.id, label: m.month }))

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <Helmet>
                <title>Calendrier Académique - Université de Ngozi</title>
                <meta
                    name="description"
                    content="Calendrier académique de l'Université de Ngozi. Restez informé des dates clés de l'année universitaire : rentrée, cours, examens et congés."
                />
            </Helmet>

            {/* Hero Section */}
            <header className="relative min-h-[50vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {heroImage && (
                        <img
                            src={heroImage}
                            alt="Calendrier Académique - Université de Ngozi"
                            className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-16">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-6 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Espaces Étudiants</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Organisation</span>
                        </div>
                        <h1 className="heading-1 mb-6 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Calendrier</span>
                            <span className="block text-white mt-2">Académique</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-6 mb-8 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-sm md:text-base font-light leading-relaxed text-primary-foreground/90">
                                Planifiez votre année universitaire avec précision grâce à notre calendrier officiel regroupant tous les événements majeurs de l'UNG.
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            <article className="container py-12 lg:py-16 max-w-5xl mx-auto">
                <main className="space-y-12">
                    {/* Breadcrumb & Quick Nav */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 -mt-4 mb-2">
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                            <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                            <span>/</span>
                            <Link to="/etudiants/blog" className="hover:text-primary transition-colors">Étudiants</Link>
                            <span>/</span>
                            <span className="text-foreground font-medium">Calendrier Académique</span>
                        </div>

                        <nav className="flex flex-wrap items-center gap-2">
                            <span className="text-[9px] uppercase tracking-widest font-black text-muted-foreground mr-1">Aller à</span>
                            {navSections.map((item) => (
                                <a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    className="px-2 py-0.5 rounded text-[10px] font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all"
                                >
                                    {item.label}
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Filter Bar */}
                    <section className="bg-secondary/5 border border-border p-4 rounded-2xl flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-2">
                            <Filter className="w-3 h-3" />
                            Filtrer par
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {filterTypes.map(type => (
                                <button
                                    key={type}
                                    onClick={() => setActiveFilter(type)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-bold transition-all border ${activeFilter === type
                                            ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                                            : 'bg-background text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
                                        }`}
                                >
                                    {type === "All" ? "Tous les événements" : type}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="scroll-mt-24">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-xl lg:text-2xl font-serif font-black italic text-foreground flex items-center gap-2">
                                <div className="w-8 h-0.5 bg-accent rounded-full" />
                                Dates Clés de l'Année
                            </h2>
                            <div className="hidden sm:flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                                <CalendarIcon className="w-4 h-4 text-accent" />
                                {activeFilter === "All" ? "Vue complète" : `Vue : ${activeFilter}`}
                            </div>
                        </div>

                        <div className="space-y-16">
                            {filteredEvents.length > 0 ? filteredEvents.map((monthGrp, idx) => (
                                <div key={idx} id={monthGrp.id} className="scroll-mt-24">
                                    <div className="flex items-center gap-4 mb-6">
                                        <h3 className="text-xl font-serif font-black italic text-foreground whitespace-nowrap">
                                            {monthGrp.month}
                                        </h3>
                                        <div className="h-px w-full bg-gradient-to-r from-border to-transparent" />
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        {monthGrp.items.map((event, i) => (
                                            <div key={i} className="group relative p-6 bg-secondary/5 rounded-2xl border border-border hover:border-accent/30 hover:shadow-xl hover:shadow-accent/5 transition-all duration-500 overflow-hidden">
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 group-hover:bg-accent/5 transition-colors duration-500" />

                                                <div className="relative flex gap-5 items-start">
                                                    <div className="flex-shrink-0 w-14 h-14 bg-background border border-border rounded-xl flex flex-col items-center justify-center group-hover:border-accent/30 group-hover:scale-105 transition-all duration-500 shadow-sm">
                                                        <span className="text-xl font-black text-primary group-hover:text-accent">{event.day}</span>
                                                        <span className="text-[8px] uppercase tracking-tighter font-bold text-muted-foreground">{monthGrp.month.substring(0, 3)}</span>
                                                    </div>

                                                    <div className="flex-1 space-y-2">
                                                        <div className={`inline-block px-2.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${getTypeStyles(event.type)}`}>
                                                            {event.type}
                                                        </div>
                                                        <h4 className="text-sm font-black text-foreground group-hover:text-primary transition-colors leading-tight">
                                                            {event.title}
                                                        </h4>
                                                        {event.detail && (
                                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                                {event.detail}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500">
                                                        <ChevronRight className="w-5 h-5 text-accent" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )) : (
                                <div className="py-20 text-center space-y-4 bg-secondary/5 rounded-3xl border border-dashed border-border">
                                    <div className="w-16 h-16 bg-background rounded-full flex items-center justify-center mx-auto border border-border">
                                        <CalendarIcon className="w-8 h-8 text-muted-foreground/30" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm font-bold text-foreground">Aucun événement trouvé</p>
                                        <p className="text-xs text-muted-foreground">Essayez de changer les filtres pour voir d'autres dates.</p>
                                    </div>
                                    <Button variant="ghost" onClick={() => setActiveFilter("All")} className="text-xs font-bold text-primary">
                                        Réinitialiser les filtres
                                    </Button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Note Importante */}
                    <div className="bg-primary/5 p-8 border border-primary/10 rounded-3xl relative overflow-hidden mt-12 group">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-accent/5 rounded-full -mr-24 -mt-24 group-hover:scale-110 transition-transform duration-700" />
                        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:rotate-12 transition-transform">
                                <Clock className="w-6 h-6 text-primary" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-serif font-black italic text-lg text-foreground flex items-center gap-2">
                                    Note Importante
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                                    Le calendrier académique est le document de référence pour l'organisation de votre année. Toutefois, il est <strong className="text-foreground">sujet à modification</strong> par la Direction Académique. Nous vous conseillons de rester attentif aux annonces officielles et de consulter régulièrement les valves de vos facultés respectives.
                                </p>
                            </div>
                        </div>
                    </div>
                </main>
            </article>
        </div>
    )
}
